import { FormErrorDisplay } from "@/components/form/form-error-display";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import UnderlineLink from "@/components/link/underline-link";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { AUTH_ACTIONS_LAYOUT_CLASS, AUTH_FORM_LAYOUT_CLASS } from "@/pages/auth-layout";
import { AUTH_CTA_CLASS, AUTH_LINK_CLASS, AuthScreenLayout } from "@/pages/auth-screen-layout";
import { APP_ROUTES } from "@/constants/app-routes";
import { useToast } from "@/hooks/ui/use-toast";
import { useRegister } from "@/hooks/use-register";
import {
  RegisterVerificationFormValues,
  RegisterVerificationSchema,
} from "@/schemas/register-verification-schema";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter, useIonViewDidEnter } from "@ionic/react";
import { useRef } from "react";
import { useForm } from "react-hook-form";

const RegisterVerificationPage = () => {
  const navigation = useIonRouter();
  const codeInputRef = useRef<HTMLInputElement>(null);

  useIonViewDidEnter(() => {
    codeInputRef.current?.focus();
  });

  const {
    isVerifyingEmailOtp,
    isResendingEmailOtp,
    verifyEmailOtpError,
    isVerifyEmailOtpError,
    verifyEmailOtpAsync,
    resendEmailOtpAsync,
  } = useRegister();
  const { showError, showSuccess } = useToast();

  const initialValues: RegisterVerificationFormValues = {
    code: "",
  };

  const searchParams = new URLSearchParams(window.location.search);
  const email = searchParams.get("email") ?? "";

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<RegisterVerificationFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(RegisterVerificationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const handleCodeBlur = () => {
    const codeValue = getValues("code");
    if (codeValue && codeValue.trim() !== "") {
      trigger("code");
    }
  };

  const handleVerifyCode = async ({ code }: RegisterVerificationFormValues) => {
    if (!email) {
      showError("Email is missing. Please register again.");
      navigation.push(APP_ROUTES.REGISTER, "root", "replace");
      return;
    }

    try {
      await verifyEmailOtpAsync({ code, email });
      showSuccess("Email verified successfully!", {
        duration: 3000,
      });
      navigation.push(APP_ROUTES.ONBOARDING_START, "forward");
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("Failed to verify email code.");
      }
    }
  };

  const handleBackToSignUp = () => {
    window.location.replace(APP_ROUTES.REGISTER);
  };

  const handleResendCode = async () => {
    try {
      await resendEmailOtpAsync();
      showSuccess("A new verification code has been sent.", {
        duration: 3000,
      });
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("Failed to resend verification code.");
      }
    }
  };

  return (
    <AuthScreenLayout
      title="Verify your email"
      subtitle={`Enter the code sent to ${email || "your email"} to complete registration.`}
    >
      <form
        className={cn(AUTH_FORM_LAYOUT_CLASS, "justify-between")}
        onSubmit={handleSubmit(handleVerifyCode)}
      >
        <div className="flex flex-col gap-8">
          <TextFieldWithLabel
            control={control}
            name="code"
            label="Code"
            placeholder="Please enter the code"
            type="text"
            autocomplete="one-time-code"
            ariaLabel="Code"
            id="code"
            inputClassName="w-full"
            error={errors.code?.message}
            showErrorMessage={false}
            scrollToOnFocus
            onBlur={handleCodeBlur}
            inputRef={codeInputRef}
            theme="dark"
          />
        </div>

        <div className={AUTH_ACTIONS_LAYOUT_CLASS}>
          <FilledButton
            disabled={!isValid || isVerifyingEmailOtp}
            text={isVerifyingEmailOtp ? "Verifying..." : "Verify Email"}
            type="submit"
            color="white"
            textCase="normal"
            className={AUTH_CTA_CLASS}
          />

          <button
            type="button"
            className={cn(
              textVariants.bodySmall,
              AUTH_LINK_CLASS,
              "mx-auto w-fit font-medium underline disabled:cursor-not-allowed disabled:opacity-60",
            )}
            onClick={handleResendCode}
            disabled={isResendingEmailOtp}
          >
            {isResendingEmailOtp ? "Sending..." : "Resend code"}
          </button>

          <FormErrorDisplay
            formErrors={errors}
            apiError={isVerifyEmailOtpError ? verifyEmailOtpError : null}
            fieldOrder={["code"]}
          />

          <p
            className={cn(
              textVariants.bodySmall,
              "mx-auto flex h-11 items-center justify-center gap-1 text-center text-white",
            )}
          >
            <span>Need to edit email? </span>
            <UnderlineLink
              text="Back to Sign Up"
              onClick={handleBackToSignUp}
              className={cn(AUTH_LINK_CLASS, "font-medium")}
            />
          </p>
        </div>
      </form>
    </AuthScreenLayout>
  );
};

export default RegisterVerificationPage;
